/**
 * @file parseBill.ts
 * @description
 * Converts a single bill block into district data dictionary
 * keyed by district id.
 */

import { VoteType } from '@/types';
import type { DistrictId, DistrictDict } from '@/types';

/**
 * Normalize vote strings to VoteType.
 */
export function normalizeVote(v?: string | null): VoteType {
  // Explicitly catch null/undefined before string normalization
  if (v == null) {
    return VoteType.NO_VOTE;
  }

  const s = v.trim().toLowerCase();

  if (s === 'yea' || s === 'yay' || s === 'yes' || s === 'aye') {
    return VoteType.YEA;
  }

  if (s === 'nay' || s === 'no') {
    return VoteType.NAY;
  }

  // Treat abstentions, absences, or explicit "no vote" as NO_VOTE
  if (s === 'present' || s === 'not voting' || s === 'no vote' || s === 'nv' || s === 'absent') {
    return VoteType.NO_VOTE;
  }

  // Fallback for anything unrecognized
  return VoteType.NO_DATA;
}

type RawDistrict = {
  voteCast?: string;
  state?: string;
  district?: number;
  name?: string;
  bioguideId?: string;
  partyName?: string;
  member_link?: string;
  img_url?: string;
  img_attribution?: string;
};

/**
 * Convert a parsed bill JSON "block" into a dictionary (Record) that maps
 * each `DistrictId` -> `DistrictData`.
 *
 * The bill data keys are already in `<congress>-<state>-<district>` format
 * (e.g., `"118-oh-12"`), but the district segment is not zero-padded and
 * at-large districts may be encoded as `"0"`.
 *
 * We normalize these keys to the canonical format used everywhere else:
 *   `<congress>-<state>-<dd>`
 * where `<dd>` is a two-digit, zero-padded district number and `"0"` is
 * treated as `"01"` for at-large / single-district cases (e.g. `"118-ak-0"`
 * becomes `"118-ak-01"`).
 *
 * @param billBlock - Raw per-district records keyed by district IDs
 * @param congressId - Congress number (kept for compatibility; should match the ID prefix)
 * @returns DistrictDict - Dictionary mapping DistrictId to district data
 */
export function parseBillToDistrictDict(
  billBlock: Record<string, RawDistrict>,
  congressId: number,
): DistrictDict {
  const out: DistrictDict = {};

  // Iterate each district entry in the raw block
  for (const [rawDistrictId, record] of Object.entries(billBlock)) {
    const parts = rawDistrictId.split('-');
    if (parts.length < 3) {
      // Skip malformed district IDs (missing district segment)
      continue;
    }

    const [congressStr, state, rawDistrictStr] = parts;
    const congressFromId = congressStr ? parseInt(congressStr, 10) : undefined;

    // Normalize district segment:
    // - "0" (at-large / single-district) -> "01"
    // - otherwise zero-pad to two digits
    const normalizedDistrictStr = rawDistrictStr === '0' ? '01' : rawDistrictStr.padStart(2, '0');

    const id = `${congressFromId ?? congressId}-${state}-${normalizedDistrictStr}` as DistrictId;
    const districtNumFromId = parseInt(normalizedDistrictStr, 10);

    // Optionally warn if the provided congressId does not match the ID prefix.
    if (
      congressFromId !== undefined &&
      !Number.isNaN(congressFromId) &&
      congressFromId !== congressId
    ) {
      console.warn(
        `[parseBillToDistrictDict] Congress mismatch for ${rawDistrictId}: ` +
          `id prefix=${congressFromId}, arg=${congressId}`,
      );
    }

    // Derive a numeric district:
    // - Prefer the explicit `record.district` field when present.
    // - Fallback to the number parsed from the normalized ID.
    // - Convert 0 to 1 for any lingering at-large encodings.
    const districtNum =
      record.district !== undefined && record.district !== null
        ? record.district === 0
          ? 1
          : record.district
        : Number.isFinite(districtNumFromId)
          ? districtNumFromId
          : undefined;

    out[id] = {
      districtId: id,
      voteRaw: record.voteCast,
      voteType: normalizeVote(record.voteCast),
      stateName: record.state,
      districtNum,
      congressId: congressFromId ?? congressId,
      legislatorName: record.name,
      legislatorId: record.bioguideId,
      legislatorParty: record.partyName,
      legislatorLink: record.member_link,
      legislatorImageLink: record.img_url,
      legislatorImageSource: record.img_attribution,
    };
  }

  return out;
}
